import { useField, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useIMask } from 'react-imask';

import { Input, InputProps } from 'theSource/components/Input';
import { FormField } from '../FormField';
import { getMask } from './utils';

export type MaskedFieldVariant = 'ssn' | 'sin' | 'phone';

export interface MaskedFieldProps
  extends Omit<InputProps, 'error' | 'onChange' | 'value'> {
  helperText?: string;
  label: string;
  name: string;
  required?: boolean;
  testId?: string;
  externalError?: boolean;
  variant: MaskedFieldVariant;
  maxLength: number;
}

export const MaskedField = ({
  disabled,
  fullWidth = true,
  helperText,
  id,
  label,
  name,
  required,
  testId = 'masked-field',
  type,
  externalError,
  variant,
  maxLength,
  ...props
}: MaskedFieldProps) => {
  const [{ ...field }, { error, touched }] = useField({
    name,
    type,
  });
  const { isSubmitting, setFieldValue } = useFormikContext();
  const invalid = useMemo(() => touched && Boolean(error), [error, touched]);

  const { ref } = useIMask(
    {
      mask: getMask(variant),
      definitions: {
        '#': /[0-9X]/g,
      },
      eager: true,
    },
    { onAccept: (value: string) => setFieldValue(field.name, value) }
  );

  return (
    <FormField
      disabled={disabled || isSubmitting}
      error={invalid || externalError}
      fullWidth={fullWidth}
      helperText={invalid ? error : helperText}
      inputId={id}
      label={label}
      required={required}
      testId={testId}
    >
      <Input
        {...props}
        {...field}
        id={id}
        type={type}
        inputProps={{ maxLength, ref }}
      />
    </FormField>
  );
};
